import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { api } from '../context/AuthContext';
import { ArrowLeft, Mail, MessageSquare } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      
      if (response.data.success) {
        setResetSent(true);
        toast.success('Password reset code sent!', {
          description: response.data.message || 'Check your phone for the reset code'
        });
      } else {
        toast.error(response.data.message || 'Failed to send reset code');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(
        error.response?.data?.detail || 
        'Failed to process request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_barber-cape-coral/artifacts/b9xk2g3p_761.png" 
              alt="Sauce Sity Logo" 
              className="h-28 w-28 object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white text-center">
            {resetSent ? 'Check Your Phone' : 'Forgot Password?'}
          </CardTitle>
          <CardDescription className="text-zinc-400 text-center">
            {resetSent 
              ? 'We sent a 6-digit code to your email address'
              : 'Enter your email and we\'ll send you a reset code'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!resetSent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <MessageSquare className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-300">
                    <p className="font-medium text-blue-400 mb-1">Email Verification</p>
                    <p>We'll send a 6-digit code to your registered email address. Make sure you have access to your email.</p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-zinc-400 hover:text-lime-400 inline-flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-lime-900/20 border border-lime-500/30 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Mail className="w-5 h-5 text-lime-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-zinc-300">
                    <p className="font-medium text-lime-400 mb-1">Code Sent!</p>
                    <p>Check your email for a 6-digit reset code. Use it on the reset password page.</p>
                  </div>
                </div>
              </div>

              <Link to="/reset-password">
                <Button className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700">
                  Enter Reset Code
                </Button>
              </Link>

              <div className="text-center">
                <button
                  onClick={() => setResetSent(false)}
                  className="text-sm text-zinc-400 hover:text-lime-400"
                >
                  Didn't receive code? Resend
                </button>
              </div>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-zinc-400 hover:text-lime-400 inline-flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
